import { createContext, type PointerEventHandler, type ReactNode, useContext, useId } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { OpacityBlackColors } from '@zep-us/zus/tokens';
const OPEN_DELAY_MS = 500;
/** Content 의 `duration-150` 과 같은 값 */
const FADE_OUT_MS = 150;
/** Radix 가 꼬리 높이(7)를 따로 더해 카드~트리거가 시안대로 20px 이 된다 */
const SIDE_OFFSET = 13;
const ARROW_WIDTH = 13;
const ARROW_HEIGHT = 7;

/**
 * ZUS shadow/lg 를 SVG 로 — stdDeviation 은 CSS blur 의 절반. 색(8자리 hex)은 flood-color 속성이
 * 아니라 style 로 넘긴다 — 속성으로 주면 Chrome 이 불투명 검정으로 그린다.
 */
const KEY_SHADOW = {
  dy: 4,
  stdDeviation: 6,
  color: OpacityBlackColors[2]
};
const AMBIENT_SHADOW = {
  dy: 0,
  stdDeviation: 8,
  color: OpacityBlackColors[0]
};
const SHADOW_BLEED = 30;
const HoverPreviewGroupContext = createContext(false);

/** 묶인 트리거끼리는 말풍선이 떠 있을 때 옆으로 옮기면 지연 없이 바꿔 띄운다 */
export const HoverPreviewGroup = ({
  children
}: {
  children: ReactNode;
}) => <TooltipPrimitive.Provider delayDuration={OPEN_DELAY_MS} skipDelayDuration={FADE_OUT_MS} disableHoverableContent data-sentry-element="unknown" data-sentry-component="HoverPreviewGroup" data-sentry-source-file="HoverPreview.tsx">
    <HoverPreviewGroupContext.Provider value={true} data-sentry-element="unknown" data-sentry-source-file="HoverPreview.tsx">
      {children}
    </HoverPreviewGroupContext.Provider>
  </TooltipPrimitive.Provider>;

/** Radix 위치 wrapper 까지 포인터를 통과시켜, 사라지는 말풍선이 밑의 트리거 호버를 가로채지 않게 한다 */
const passPointerThrough = (content: HTMLDivElement | null) => {
  if (content?.parentElement) content.parentElement.style.pointerEvents = 'none';
};
type HoverPreviewProps = {
  preview: ReactNode;
  /** 스크린 리더용 트리거 설명. 없으면 Radix 가 preview 를 숨은 사본으로 한 번 더 그린다 */
  description: string;
  /** asChild 로 붙으므로 ref 를 받는 요소 하나 */
  children: ReactNode;
  onOpenChange?: (open: boolean) => void;
  onTriggerPointerEnter?: PointerEventHandler;
};

/**
 * 트리거에 호버하면 위쪽에 꼬리 달린 말풍선을 띄운다(Radix Tooltip). body 로 포털한다 — 빌드 화면
 * 조상의 transform 에 위치·잘림이 묶이지 않게.
 */
export const HoverPreview = ({
  preview,
  description,
  children,
  onOpenChange,
  onTriggerPointerEnter
}: HoverPreviewProps) => {
  const isInGroup = useContext(HoverPreviewGroupContext);
  const arrowId = useId().replace(/:/g, '');
  const filterId = `hover-preview-arrow-shadow-${arrowId}`;
  const clipPathId = `hover-preview-arrow-clip-${arrowId}`;
  const hoverPreview = <TooltipPrimitive.Root onOpenChange={onOpenChange}>
      <TooltipPrimitive.Trigger asChild onPointerEnter={onTriggerPointerEnter}>
        {children}
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content ref={passPointerThrough} side="top" align="center" sideOffset={SIDE_OFFSET} aria-label={description} className="pointer-events-none z-[99999] duration-150 data-[state=delayed-open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2 motion-reduce:data-[state=closed]:animate-none motion-reduce:data-[state=delayed-open]:animate-none">
          {preview}
          <TooltipPrimitive.Arrow asChild width={ARROW_WIDTH} height={ARROW_HEIGHT}>
            <svg className="overflow-visible fill-white" viewBox="0 0 13 6.82838" preserveAspectRatio="none">
              <defs>
                <filter id={filterId} x={-SHADOW_BLEED} y={-SHADOW_BLEED} width={ARROW_WIDTH + SHADOW_BLEED * 2} height={SHADOW_BLEED * 2 + ARROW_HEIGHT} filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feDropShadow dx="0" dy={KEY_SHADOW.dy} stdDeviation={KEY_SHADOW.stdDeviation} style={{
                  floodColor: KEY_SHADOW.color
                }} result="key" />
                  <feDropShadow in="SourceGraphic" dx="0" dy={AMBIENT_SHADOW.dy} stdDeviation={AMBIENT_SHADOW.stdDeviation} style={{
                  floodColor: AMBIENT_SHADOW.color
                }} result="ambient" />
                  <feMerge>
                    <feMergeNode in="ambient" />
                    <feMergeNode in="key" />
                  </feMerge>
                </filter>
                <clipPath id={clipPathId}>
                  <rect x={-SHADOW_BLEED} y="0" width={ARROW_WIDTH + SHADOW_BLEED * 2} height={SHADOW_BLEED + ARROW_HEIGHT} />
                </clipPath>
              </defs>
              <g clipPath={`url(#${clipPathId})`}>
                {/* 시안 path 를 위아래로 뒤집은 좌표 — CSS 로 뒤집으면 그림자 방향도 뒤집힌다 */}
                <path filter={`url(#${filterId})`} d="M4.94777 6.089565C5.74815 7.074652 7.25184 7.074651 8.05223 6.089565L13 0H0L4.94777 6.089565Z" />
              </g>
            </svg>
          </TooltipPrimitive.Arrow>
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>;
  return isInGroup ? hoverPreview : <HoverPreviewGroup data-sentry-element="HoverPreviewGroup" data-sentry-component="HoverPreview" data-sentry-source-file="HoverPreview.tsx">{hoverPreview}</HoverPreviewGroup>;
};